import React from 'react';
import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

export interface PrimaryButtonProps extends Omit<MuiButtonProps, 'variant' | 'color'> {
  loading?: boolean;
  loadingLabel?: string;
}

const SIZE_SX = {
  small: { minHeight: 32, px: '12px', py: '6px' },
  medium: { minHeight: 36, px: '14px', py: '8px' },
  large: { minHeight: 40, px: '16px', py: '10px', fontSize: 14 },
} as const;

const PrimaryButton = React.forwardRef<HTMLButtonElement, PrimaryButtonProps>(
  ({ loading, loadingLabel, disabled, children, sx, size = 'small', ...props }, ref) => (
    <MuiButton
      ref={ref}
      variant="contained"
      size={size}
      disabled={disabled || loading}
      disableElevation
      sx={{
        borderRadius: '8px',
        fontWeight: 500,
        fontSize: 13,
        lineHeight: '20px',
        textTransform: 'none',
        gap: '6px',
        boxShadow: 'none',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        ...SIZE_SX[size],
        '& .MuiButton-startIcon, & .MuiButton-endIcon': { m: 0 },
        '& .MuiButton-startIcon > *:nth-of-type(1), & .MuiButton-endIcon > *:nth-of-type(1)': { fontSize: 16 },
        '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
        '&.Mui-disabled': { bgcolor: 'primary.main', color: 'primary.contrastText', opacity: 0.45 },
        ...sx,
      }}
      {...props}
    >
      {loading ? (
        <>
          <CircularProgress size={14} color="inherit" />
          {loadingLabel ?? children}
        </>
      ) : children}
    </MuiButton>
  )
);

PrimaryButton.displayName = 'PrimaryButton';
export default PrimaryButton;
